import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { getAgentCabinetBookings } from "../../api/agentCabinet";

const AgentBookingsPage = () => {
  const { t } = useTranslation("common");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getAgentCabinetBookings();
        const list = Array.isArray(data) ? data : data.results || [];
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) setError(t("agent.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <MuiLink
        component={RouterLink}
        to="/agent"
        underline="hover"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
      >
        <ArrowBackIcon fontSize="inherit" />
        {t("agent.dashboardTitle")}
      </MuiLink>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("agent.myBookings")}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t("agent.noBookings")}</Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("agent.colNumber")}</TableCell>
                <TableCell>{t("agent.colRoute")}</TableCell>
                <TableCell>{t("agent.colDate")}</TableCell>
                <TableCell>{t("agent.colStatus")}</TableCell>
                <TableCell align="right">{t("agent.colPrice")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.booking_number}</TableCell>
                  <TableCell>
                    {row.from_city} → {row.to_city}
                  </TableCell>
                  <TableCell>
                    {row.departure_date
                      ? new Date(row.departure_date).toLocaleString("uk-UA")
                      : "—"}
                  </TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell align="right">
                    {row.total_price} {row.currency}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Container>
  );
};

export default AgentBookingsPage;
